import type { BasicStoreFragment } from '@drycut/graphql';

import { useAuth } from '@drycut/auth';
import { StoreWaitingTime } from '@drycut/controlled-components';
import { Button, DrycutLogo, Para, Skeleton, Stack } from '@drycut/ui-components';
import { ClockIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router';
import { useBoolean } from 'usehooks-ts';

import { CheckInModal } from './CheckInModal';
import { CreateCouponModal } from './CreateCouponModal';
import { useStoreContext } from './useStoreContext';

type Props = {
  actionButton: React.ReactElement;
};

export function DashboardHeader({ actionButton }: Props): React.ReactElement {
  const { store, loading } = useStoreContext();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const loaded = !loading && !!store;
  const { value: couponModalOpen, toggle: toggleCouponModal } = useBoolean(false);

  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  return (
    <header className="flex flex-col justify-between gap-4">
      <Stack direction="row" className="justify-between">
        <DrycutLogo />

        <Stack direction="row" gap={4}>
          <CheckInModal />

          <Button fill="outlined" onClick={toggleCouponModal}>
            Gutschein
          </Button>

          <Button className="w-52" color="secondary" onClick={handleLogout}>
            Log out
          </Button>
        </Stack>
      </Stack>

      <CreateCouponModal open={couponModalOpen} onClose={toggleCouponModal} />

      <Stack direction="row" className="justify-between">
        <Stack>
          <Stack direction="row" gap={2}>
            <Para size="lg" weight="bold">
              Salon:
            </Para>

            {loaded && (
              <Para size="lg">
                {store.location.street}, {store.location.zipCode}, {store.location.city}
              </Para>
            )}

            {!loaded && <Skeleton />}
          </Stack>

          <Stack direction="row" gap={2}>
            <ClockIcon className="h-5 w-5 text-primary-600" />

            <Para className="text-primary-600">Wartezeit / nächster Termin:</Para>

            <Para weight="bold">
              {loaded && (
                <StoreWaitingTime store={store as BasicStoreFragment} showCa showZero long />
              )}
            </Para>
          </Stack>
        </Stack>

        {actionButton}
      </Stack>
    </header>
  );
}
